<template>
  <div class="row">
    <div class="col-12">
      <div class="card">
        <!-- 헤더 -->
        <DetailHeader :headerTitle="headerTitle" :backTo="'Products'"/>

        <!-- 바디 -->
        <div class="pt-3 pb-4 d-flex justify-content-center">
          <div v-if="isEdit && loading">
            <TableBodyLoader :minHeight="578" :offsetY="-50" message="상세정보를 불러오는 중..." />
          </div>
          <div v-else class="w-100 px-5">

            <div class="row gx-4">
              <div class="col-lg-8 col-md-7" data-tour="dcreate.left">
                <section class="card border-0 h-100">
                  <div class="card-body">
                    <h6 class="mb-3">상세정보</h6>
                    <!-- 썸네일 -->
                    <ImagePicker
                      class="mb-3"
                      v-model:file="form.imgFile"
                      :existing-preview="form.imgPreview"
                      :existing-name="form.imgFilename"
                      :max-size="10 * 1024 * 1024"
                      label="대표 이미지"
                      alt="scanplace"
                      @selected="onImgSelected"
                      @cleared="onImgCleared"
                      @error="toastError"
                    />

                  <!-- 필드 -->
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">카테고리 <span class="text-danger">*</span></label>
                      <select class="form-select" v-model="form.category" data-tour="product.category">
                        <option :value="''" disabled>카테고리를 선택하세요</option>
                        <option
                          v-for="c in categoryList"
                          :key="c.id ?? c.categoryCode ?? c"
                          :value="c.categoryCode ?? c"
                        >
                          {{ c.categoryName ?? c }}<template v-if="c.categoryCode"> ({{ c.categoryCode }})</template>
                        </option>
                      </select>
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">GTIN <span class="text-danger">*</span></label>
                      <input
                        type="text"
                        class="form-control"
                        v-model.trim="form.gtin"
                        inputmode="numeric"
                        @input="onGtinInput"
                        placeholder="숫자만 입력 (예: 08801234567890)"
                      />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">제품명 <span class="text-danger">*</span></label>
                      <input type="text" class="form-control" v-model.trim="form.name" placeholder="예: Ultra Lite Glasses" />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">단위</label>
                      <select class="form-select" v-model="form.unit" data-tour="product.unit">
                        <option :value="''" disabled>단위를 선택하세요</option>
                        <option
                          v-for="u in unitList"
                          :key="u.code ?? u"
                          :value="u.code ?? u"
                        >
                          {{ u.codeNm ?? u }} <template v-if="u.code">({{ u.code }})</template>
                        </option>
                      </select>
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">EPC 타입 <span class="text-danger">*</span></label>
                      <select class="form-select" v-model="form.epcType" data-tour="product.epcType">
                        <option :value="''" disabled>EPC 타입 선택</option>
                        <option
                          v-for="e in epcList"
                          :key="e.code ?? e"
                          :value="e.code ?? e"
                        >
                          {{ e.name ?? e }}
                        </option>
                      </select>
                    </div>

                    <div class="col-12">
                      <label class="form-label">설명</label>
                      <textarea class="form-control" rows="3" v-model.trim="form.dsc" placeholder="제품 설명을 입력하세요."></textarea>
                    </div>
                  </div>

                  <!-- 메타 -->
                  <div class="row g-3 mt-1 mb-3" v-if="isEdit">
                    <div class="col-md-6">
                      <label class="form-label">생성일시</label>
                      <input type="text" class="form-control" :value="formatDate(form.createdAt) || '-'" disabled />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">수정일시</label>
                      <input type="text" class="form-control" :value="formatDate(form.updatedAt) || '-'" disabled />
                    </div>
                  </div>
                </div>
            </section>
                </div>

              <!-- ▼▼ 오른쪽: 탄소배출량 슬라이더 ▼▼ -->
              <div class="col-lg-4 col-md-5 mt-3 mt-md-0 d-flex flex-column" data-tour="dcreate.co2">
                <co2-slider
                  v-model="carbonEmissionLocal"
                  :min="emissionMin"
                  :max="emissionMax"
                  :step="emissionStep"
                  title="탄소배출량"
                  hint="드래그로 탄소 배출량을 조절하세요."
                  :autoExpandMax="true"
                  :expandUnit="50"
                />
                <co2-info kind="product"/>
              </div>

            </div> <!-- /row -->

                <!-- 액션 -->
                <div class="d-flex justify-content-between mt-5 mb-3 gap-2 px-2">
                  <!-- 왼쪽: 목록가기 -->
                  <soft-button 
                    color="outline-secondary" 
                    @click="$router.push({ name: 'Products', query: { ...$route.query } })"
                  >
                    목록
                  </soft-button>
                  <!-- 오른쪽: 삭제/저장 -->
                  <div class="d-flex gap-2">
                    <soft-button v-if="isEdit" color="danger" @click="remove" :disabled="loading">삭제</soft-button>
                    <soft-button color="success" @click="save" :disabled="loading" data-tour="dcreate.save">저장</soft-button>
                  </div>
                </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <ProductTour />
<SoftConfirm ref="confirm" />
</template>

<script>
import SoftConfirm from "@/components/SoftConfirm.vue";
import DetailHeader from "@/components/DetailHeader.vue";
import SoftButton from "@/components/SoftButton.vue";
import TableBodyLoader from "@/components/loading/TableBodyLoader.vue";
import ImagePicker from "@/views/components/common/ImagePicker.vue";
import Co2Info from "@/views/components/co2/Co2Info.vue";
import Co2Slider from "@/views/components/co2/Co2Slider.vue";
import ProductTour from "@/components/tour/ProductTour.vue";
import api from "@/api/api";

export default {
  name: "ProductDetail",
  components: { SoftConfirm, DetailHeader, SoftButton, TableBodyLoader, ImagePicker, Co2Info, Co2Slider, ProductTour },
  inject: ['showLoading', 'hideLoading'],
  props: {
    mode: { type: String, default: "create" },
    id: { type: [String, Number], default: "" }
  },
  data() {
    return {
      loading: false,
      form: {
        id: null,
        category: "",
        gtin: "",
        dsc: "",
        epcType: "",
        name: "",
        unit: "",
        mfId: "",
        atchFileId: "",
        imgFile: null,
        imgPreview: "",
        imgFilename: "",
        carbonEmission: "0",
        createdAt: "",
        updatedAt: ""
      },
      categoryList: [],
      manufactureInfo: {},
      unitList: [],
      epcList: ["SGTIN"],
      imgChanged: false,
      imgRemoved: false, 
        
      emissionMin: 0,
      emissionMax: 10,
      emissionStep: 0.01,
      carbonEmissionLocal: 0,
    };
  },
  computed: {
    isEdit() { return this.mode === "edit" && !!this.id; },
    headerTitle() { return this.isEdit ? "제품정보 수정" : "제품정보 등록"; },
    displayFileName() {
      return this.form.imgFile?.name || this.form.imgFilename || "";
    },
  },
  watch: {
    'form.carbonEmission'(val) {
      const num = Number(val);
      if (!Number.isNaN(num)) {
        const fixed = Number(num.toFixed(2));
        if (fixed !== this.carbonEmissionLocal) this.carbonEmissionLocal = fixed;
        if (fixed > this.emissionMax) this.emissionMax = Math.ceil(fixed / 50) * 50;
      }
    },
    carbonEmissionLocal(val) {
      const fixedStr = Number(val).toFixed(2);
      if (fixedStr !== this.form.carbonEmission) this.form.carbonEmission = fixedStr;
    },
  },
  async mounted() {
    this.form.mfId = this.$store.getters['UserStore/getMfId'];
    this.initData();
    if (this.isEdit) await this.fetchDetail();

    const v = Number(this.form.carbonEmission || 0);
    this.carbonEmissionLocal = Number.isFinite(v) ? Number(v.toFixed(2)) : 0;
    if (this.carbonEmissionLocal > this.emissionMax) {
      this.emissionMax = Math.ceil(this.carbonEmissionLocal / 50) * 50;
    }

    this._tourImgHandler = (e) => {
      const { file, blob, name } = e.detail || {};
      if (!file || !blob) return;

      if (this.form.imgPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.form.imgPreview);
      }
      const previewUrl = URL.createObjectURL(blob);

      this.form.imgFile     = file;
      this.form.imgPreview  = previewUrl;
      this.form.imgFilename = name || file.name || 'demo.jpg';

      this.imgChanged = true;
      this.imgRemoved = false;
    };
    window.addEventListener('tour:setImage', this._tourImgHandler);
  },
  beforeUnmount() {
    if (this.form.imgPreview) URL.revokeObjectURL(this.form.imgPreview);
    window.removeEventListener('tour:setImage', this._tourImgHandler);
    if (this.form.imgPreview?.startsWith('blob:')) URL.revokeObjectURL(this.form.imgPreview);
  },
  methods: {
    async initData() {
      try {
        const mfId = this.$store.getters['UserStore/getMfId'];
        const { data } = await api.get(`/product/initData`, { params: { mfId } });
        const result = data?.result || {};
        this.categoryList = result.categoryList || [];
        this.unitList = result.unitList?.detailCodes || result.unitList || [];
        this.epcList = result.epcList || ["SGTIN"];
      } catch (error) {
        console.error("초기 데이터 불러오기 실패:", error);
      }
    },

    async fetchDetail() {
      this.loading = true;
      try {
        const [res] = await Promise.all([
          api.get(`/product`, { params: { id: this.id } }),
          new Promise(r => setTimeout(r, 300))
        ]);
        const item = res.data?.result?.productInfo || {};
        const files = res.data?.result?.files || [];
        const manufactureInfo = res.data?.result?.manufactureInfo || {};
        Object.assign(this.form, item);
        this.manufactureInfo = manufactureInfo;
        
        if (files.length > 0) {
          this.form.imgPreview  = files[0].downloadUrl || "";
          this.form.imgFilename = files[0].originalName || "";
        } else {
          this.form.imgPreview  = "";
          this.form.imgFilename = "";
        }
        this.files = files;

        this.carbonEmissionLocal = Number(this.form.carbonEmission || 0) || 0;
        if (this.carbonEmissionLocal > this.emissionMax) {
          this.emissionMax = Math.ceil(this.carbonEmissionLocal / 50) * 50;
        }
      } catch (e) {
        this.toastError?.("상세 조회 중 오류가 발생했습니다.");
      } finally {
        this.loading = false;
      }
    },
    onImgSelected({ file, previewUrl, name }) {
      if (this.form.imgPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.form.imgPreview);
      }
      this.form.imgFile     = file;
      this.form.imgPreview  = previewUrl;
      this.form.imgFilename = name;

      this.imgChanged = true;
      this.imgRemoved = false;
    },
    onImgCleared() {
      if (this.form.imgPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.form.imgPreview);
      }
      this.form.imgFile     = null;
      this.form.imgPreview  = "";
      this.form.imgFilename = "";

      this.imgChanged = false;
      this.imgRemoved = true;
    },
    onGtinInput(e) {
      // 숫자만 허용
      const onlyDigits = e.target.value.replace(/\D/g, "");
      e.target.value = onlyDigits;
      this.form.gtin = onlyDigits;
    },
    buildFormData() {
      const fd = new FormData();
      fd.append("name", this.form.name ?? "");
      fd.append("gtin", this.form.gtin ?? "");
      fd.append("epcType", this.form.epcType ?? "");
      if (this.form.dsc)            fd.append("dsc", this.form.dsc);
      if (this.form.category)        fd.append("category", this.form.category);
      if (this.form.unit)   fd.append("unit", this.form.unit);
      if (this.form.mfId !== null && this.form.mfId !== undefined) fd.append("mfId", String(this.form.mfId));
      if (this.form.atchFileId) fd.append("atchFileId", this.form.atchFileId);
      if (this.form.carbonEmission)  fd.append("carbonEmission", Number(this.form.carbonEmission || 0).toFixed(2));
      fd.append("imgChanged", this.imgChanged ? "Y" : "N");
      fd.append("imgRemoved", this.imgRemoved ? "Y" : "N");
      if (this.form.imgFile) fd.append("file", this.form.imgFile);
      return fd;
    },

    async save() {
      if (!this.form.name?.trim()) { this.toastError?.("제품명을 입력하세요."); return; }
      if (!this.form.gtin?.trim()) { this.toastError?.("GTIN을 입력하세요."); return; }
      if (!/^\d+$/.test(this.form.gtin)) { this.toastError?.("GTIN은 숫자만 입력하세요."); return; }
      if (!this.form.epcType?.trim()) { this.toastError?.("EPC 타입을 선택하세요."); return; }

      const action = this.isEdit ? '수정' : '등록';
      const ok = await this.$refs.confirm.open({
        title: `제품정보 ${action}`,
        message: `${action} 하시겠습니까?`,
        confirmText: action,
        cancelText: "취소",
        confirmColor: "success",
      });
      if (!ok) return;

      const delay = (ms) => new Promise(r => setTimeout(r, ms));
      const started = performance.now();

      this.showLoading?.('저장중...');
      try {
        const fd = this.buildFormData();
        if (this.isEdit) {
          await api.put(`/product/${this.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } else {
          const { data } = await api.post(`/product`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          this.form.id = data?.result?.id;
        }

        const elapsed = performance.now() - started;
        if (elapsed < 1000) await delay(1000 - elapsed);

        this.toastSuccess?.(`제품정보가 ${action}되었습니다.`);
        this.$router.push({
          name: "ProductEdit",
          params: { id: this.form.id },
          query: { ...this.$route.query, t: Date.now()},
        });

      } catch (e) {
        console.log(e)
        this.toastError?.(`${action} 중 오류가 발생했습니다.`);
      } finally {
        this.hideLoading?.();
      }
    },

    async remove() {
      if (!this.isEdit) return;

      const ok = await this.$refs.confirm.open({
        title: "삭제 확인",
        message: "정말 삭제하시겠습니까?",
        confirmText: "삭제",
        cancelText: "취소",
        confirmColor: "danger",
      });
      if (!ok) return;
  
      this.showLoading?.('삭제중...');
      try {
        const delay = ms => new Promise(r => setTimeout(r, ms));
        await Promise.all([
          api.delete(`/product/${this.id}`),
          delay(500),
        ]);

        this.toastSuccess?.("삭제되었습니다.");
        this.$router.push({
          name: "Products",
          query: { ...this.$route.query }, 
        });
      } catch (e) {
        this.toastError?.("삭제 중 오류가 발생했습니다.");
      } finally {
        this.hideLoading?.();
      }
    }
  }
};
</script>

<style scoped>
.card { border: 1px solid rgba(0,0,0,.06) !important; }

</style>
